import { useTeamsCache } from "./caching/use-teams-cache";
import { useProjectsCache } from "./caching/use-projects-cache";
import { useBoardMembersCache } from "./caching/use-boards-members-cache";
import { useUsersCache } from "./caching/use-users-cache";
import { useEffect } from "react";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { User } from "shared/datamodel/schemas";
import { Team } from "shared/datamodel/schemas/team";
import { Project } from "shared/datamodel/schemas/project";
import { BoardMembers } from "shared/datamodel/schemas/board";
import useSeatsCount from "./use-seats-count";
import useStateValue from "frontend/state/value";
import useSafeState from "./use-safe-state";

const INIT_SECTION_ID = "none";

export function useInstanceMembers(instanceId: string | null, instanceType: InstanceType) {
  const [{ user }] = useStateValue();
  const { getTeams, updateTeam } = useTeamsCache();
  const { getProjects, updateProject } = useProjectsCache();
  const { getBoardMembers, updateBoardMembers } = useBoardMembersCache();
  const { getUsers } = useUsersCache();
  const [members, setMembers] = useSafeState<User[]>([]);
  const [membersCount, setMembersCount] = useSafeState<number>(0);
  const [isLoading, setIsLoading] = useSafeState(false);
  const { userAccountCount } = useSeatsCount(user);

  const setTeamMembers = async (team: Team) => {
    if (!team?.userTeamPermissions) {
      return;
    }
    if (team?.accountTeam) {
      setMembers([]);
      setMembersCount(userAccountCount);
      return;
    }
    const users = await getUsers(team.userTeamPermissions.map((m) => m.id));
    const members = users
      .map((user) => ({
        ...user,
        permission: team.userTeamPermissions.find((m) => m.id === user.id)?.permission,
      }))
      .filter((user) => user !== null) as User[];
    setMembers(members);
    setMembersCount(members.length);
  };

  const setProjectMembers = async (project: Project) => {
    if (!project?.userProjectPermissions) {
      return;
    }
    const users = await getUsers(project.userProjectPermissions.map((m) => m.id));
    const members = users
      .map((user) => ({
        ...user,
        permission: project.userProjectPermissions.find((m) => m.id === user.id)?.permission,
      }))
      .filter((user) => user !== null) as User[];
    setMembers(members);
    setMembersCount(members.length);
  };

  const setBoardMembers = async (boardMembers: BoardMembers) => {
    if (!boardMembers?.members) {
      setMembers([]);
      setMembersCount(0);
      return;
    }

    const users = await getUsers(boardMembers.members.map((m) => m.id.toString()));
    const members = users
      .map((user) => {
        return {
          ...user,
          permission: boardMembers.members.find((m) => m.id.toString() === user.id)?.permission,
        };
      })
      .filter((user) => user !== null) as User[];
    setMembers(members);
    setMembersCount(members.length);
  };

  useEffect(() => {
    async function fetchMembers() {
      if (!instanceId || instanceId === INIT_SECTION_ID) {
        setMembers([]);
        return;
      }
      setIsLoading(true);
      switch (instanceType) {
        case InstanceType.Team:
        case InstanceType.Workspace: {
          const [team] = await getTeams([instanceId]);
          await setTeamMembers(team);
          break;
        }

        case InstanceType.Project: {
          const [project] = await getProjects([instanceId]);
          await setProjectMembers(project);
          break;
        }
        case InstanceType.Board: {
          const boardMembers = await getBoardMembers(instanceId);
          if (!boardMembers) return;
          await setBoardMembers(boardMembers);
          break;
        }
        default: {
          setMembers([]);
        }
      }
      setIsLoading(false);
    }

    fetchMembers();
  }, [instanceId, instanceType, getTeams, getProjects, getBoardMembers, getUsers]);

  const updateMembers = async (instance: Team | Project | BoardMembers, parentInstance?: Team) => {
    setIsLoading(true);
    switch (instanceType) {
      case InstanceType.Team:
      case InstanceType.Workspace: {
        await setTeamMembers(instance as Team);
        await updateTeam(instance as Team);
        break;
      }
      case InstanceType.Project: {
        if (parentInstance) {
          await setTeamMembers(parentInstance);
          await updateTeam(parentInstance);
        }
        await setProjectMembers(instance as Project);
        await updateProject(instance as Project);
        break;
      }
      case InstanceType.Board: {
        await setBoardMembers(instance as BoardMembers);
        await updateBoardMembers(instance as BoardMembers);
        break;
      }
    }
    setIsLoading(false);
  };

  return {
    members,
    isLoading,
    membersCount,
    updateMembers,
  };
}
